import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportDownloadByFilesBreakdownData,
} from '@src/types/reports';
import { TOption } from '@src/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import dayjs from 'dayjs';
import {
  exportDownloadByFilesBreakdown,
  getDownloadByFilesBreakdown,
  getDownloadByFilesBreakdownFiles,
} from '@src/services/reports';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.dataKey}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const DownloadByFilesBreakdown: FC = () => {
  const [data, setData] = useState<TReportDownloadByFilesBreakdownData[]>([]);
  const [fileOptions, setFileOptions] = useState<TOption[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableFiles: true,
    enableComparisons: true,
  });

  const displayFileNames = useMemo(() => {
    if (filters.selectedFiles.length) {
      return fileOptions
        .filter((item) => filters.selectedFiles.includes(item.value))
        .map((item) => item.label);
    }

    const responseFileNames = Array.from(
      data.reduce((acc, item) => {
        Object.keys(item.files || {}).forEach((fileName) => acc.add(fileName));

        return acc;
      }, new Set<string>())
    );

    if (responseFileNames.length) {
      return responseFileNames;
    }

    return fileOptions.map((item) => item.label);
  }, [data, fileOptions, filters.selectedFiles]);

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: displayFileNames,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.files[key] || 0,
    getPreviousPeriodField: (item, key) => item.files_previous?.[key],
    getPreviousYearField: (item, key) => item.files_previous_year?.[key],
    itemsField: 'files',
    includeTotalsRow: true,
  });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.files[key] || 0,
    getPreviousPeriodField: (item, key) => item.files_previous?.[key],
    getPreviousYearField: (item, key) => item.files_previous_year?.[key],
  });

  const buildReportPayload = () => {
    const fileIds = filters.selectedFiles
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    return {
      start_date: filters.startDate
        ? dayjs(filters.startDate).format('YYYY-MM-DD')
        : null,
      end_date: filters.endDate
        ? dayjs(filters.endDate).format('YYYY-MM-DD')
        : null,
      grouping: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
      file_ids: fileIds.length ? fileIds : undefined,
      compare_to_previous_period: filters.compareToPreviousPeriod,
      compare_to_same_period_previous_year:
        filters.compareToSamePeriodPreviousYear,
    };
  };

  const handleFetchFiles = async () => {
    try {
      const files = await getDownloadByFilesBreakdownFiles();
      const options = files.map((item) => ({
        label: item.file_name,
        value: item.file_id.toString(),
      }));

      setFileOptions(options);
    } catch (error) {
      toast.error('Error fetching files');
      console.error('Error fetching files:', error);
    }
  };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildReportPayload();
      const response = await getDownloadByFilesBreakdown(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildReportPayload();
      const response = await exportDownloadByFilesBreakdown(payload, 'csv');
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `download-by-files-breakdown-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Failed to export report');
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    handleFetchFiles();
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Download by Files Breakdown
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          fileOptions={fileOptions}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedFiles={filters.selectedFiles}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          showFilesFilter={true}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onFilesChange={filters.setSelectedFiles}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {displayItems.map((file, index) => (
                <Line
                  key={file}
                  activeDot={{ r: 6 }}
                  dataKey={file}
                  dot={{ fill: getChartColor(index), r: 4 }}
                  name={file}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                displayItems.map((file, index) => (
                  <Line
                    key={`${file}_previous_period`}
                    activeDot={{ r: 4 }}
                    dataKey={`${file}_previous_period`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name={`${file} (Previous Period)`}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((file, index) => (
                  <Line
                    key={`${file}_previous_year`}
                    activeDot={{ r: 4 }}
                    dataKey={`${file}_previous_year`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name={`${file} (Previous Year)`}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportReport}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
